import { useState, useCallback, useRef } from 'react';

/**
 * useAddToList — shared hook for the "Add to List" submenu in detail modals.
 *
 * Returns a menuAction object that can be spread into the menuActions array.
 * Handles fetching lists, checking for duplicates, adding items, and creating new lists.
 *
 * @param {Object} options
 * @param {Object} options.backendService - The app's backend service instance
 * @param {string} options.activeView - Current user view name (for fetching user's lists)
 * @param {Function} options.getItemData - (item) => { itemId, title, imageURL } for the current modal item
 */
export default function useAddToList({ backendService, activeView, getItemData }) {
    const [lists, setLists] = useState([]);
    const [existingListIds, setExistingListIds] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(null); // listId currently being added to
    const [creatingNew, setCreatingNew] = useState(false);
    const [newListName, setNewListName] = useState('');
    const inputRef = useRef(null);

    const fetchLists = useCallback(async () => {
        setLoading(true);
        try {
            const result = await backendService.getAllLists(activeView);
            const allLists = result.success ? result.lists : [];
            setLists(allLists);

            // Check which lists already contain this item
            const itemData = getItemData();
            if (itemData?.itemId) {
                const checks = await Promise.all(
                    allLists.map(async (list) => {
                        const itemsResult = await backendService.getListItems(list.id);
                        const items = itemsResult.items || [];
                        const found = items.some((li) => li.itemId === itemData.itemId);
                        return found ? list.id : null;
                    })
                );
                setExistingListIds(new Set(checks.filter(Boolean)));
            } else {
                setExistingListIds(new Set());
            }
        } catch (err) {
            console.error('Failed to fetch lists:', err);
        } finally {
            setLoading(false);
        }
    }, [backendService, activeView, getItemData]);

    const handleAdd = useCallback(async (listId) => {
        setAdding(listId);
        try {
            const itemData = getItemData();
            await backendService.addListItem(listId, itemData);
            setExistingListIds((prev) => new Set([...prev, listId]));
        } catch (err) {
            console.error('Failed to add to list:', err);
        } finally {
            setAdding(null);
        }
    }, [backendService, getItemData]);

    const handleCreateAndAdd = useCallback(async () => {
        const name = newListName.trim();
        if (!name) return;
        setAdding('__new__');
        try {
            const newList = await backendService.createList(activeView, { name, emoji: '' });
            const itemData = getItemData();
            await backendService.addListItem(newList.id, itemData);
            // Refresh lists to show the new one
            setLists((prev) => [...prev, newList]);
            setExistingListIds((prev) => new Set([...prev, newList.id]));
            setCreatingNew(false);
            setNewListName('');
        } catch (err) {
            console.error('Failed to create list:', err);
        } finally {
            setAdding(null);
        }
    }, [backendService, activeView, getItemData, newListName]);

    const renderSubmenu = useCallback(({ close }) => {
        if (loading) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div className="w-4 h-4 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
                </div>
            );
        }

        return (
            <div className="max-h-48 overflow-y-auto">
                {lists.map((list) => {
                    const alreadyIn = existingListIds.has(list.id);
                    const isAdding = adding === list.id;
                    return (
                        <button
                            key={list.id}
                            onClick={() => {
                                if (alreadyIn || isAdding) return;
                                handleAdd(list.id);
                            }}
                            disabled={alreadyIn || isAdding}
                            className={[
                                'w-full text-left px-4 py-2 text-sm transition-colors duration-100 flex items-center gap-2',
                                alreadyIn
                                    ? 'text-text-tertiary cursor-default'
                                    : 'text-text-primary hover:bg-surface-hover',
                            ].join(' ')}
                        >
                            {list.emoji && <span className="flex-shrink-0">{list.emoji}</span>}
                            <span className="truncate flex-1">{list.name}</span>
                            {isAdding ? (
                                <div className="w-3.5 h-3.5 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                            ) : alreadyIn ? (
                                <svg className="h-3.5 w-3.5 text-accent-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                </svg>
                            ) : null}
                        </button>
                    );
                })}

                {/* Divider before "New list" */}
                {lists.length > 0 && <div className="border-t border-border-subtle my-1" />}

                {/* Create new list */}
                {creatingNew ? (
                    <div className="px-3 py-2">
                        <form
                            onSubmit={(e) => { e.preventDefault(); handleCreateAndAdd(); }}
                            className="flex items-center gap-2"
                        >
                            <input
                                ref={inputRef}
                                type="text"
                                value={newListName}
                                onChange={(e) => setNewListName(e.target.value)}
                                placeholder="List name..."
                                autoFocus
                                className="flex-1 min-w-0 px-2 py-1 text-sm rounded bg-bg-primary border border-border text-text-primary placeholder-text-tertiary focus:outline-none focus:ring-1 focus:ring-accent-primary"
                            />
                            <button
                                type="submit"
                                disabled={!newListName.trim() || adding === '__new__'}
                                className="flex-shrink-0 p-1 text-accent-primary hover:text-accent-primary-hover disabled:text-text-tertiary transition-colors"
                            >
                                {adding === '__new__' ? (
                                    <div className="w-4 h-4 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                    </svg>
                                )}
                            </button>
                        </form>
                    </div>
                ) : (
                    <button
                        onClick={() => setCreatingNew(true)}
                        className="w-full text-left px-4 py-2 text-sm text-accent-primary hover:bg-surface-hover transition-colors duration-100 flex items-center gap-2"
                    >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                        New list
                    </button>
                )}
            </div>
        );
    }, [loading, lists, existingListIds, adding, creatingNew, newListName, handleAdd, handleCreateAndAdd]);

    /** The menu action object to spread into menuActions */
    const addToListAction = {
        key: 'add-to-list',
        label: 'Add to List',
        onOpen: fetchLists,
        renderSubmenu,
    };

    return { addToListAction };
}
